import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/Delete';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import ShareIcon from '@material-ui/icons/Share';
import DateRangeIcon from '@material-ui/icons/DateRange';
import PlaylistAddIcon from '@material-ui/icons/PlaylistAdd';
import EventAvailableIcon from '@material-ui/icons/EventAvailable';
import { Link } from 'react-router-dom';
import EmptyState from '../components/common/EmptyState';
import PlaceCardV2 from '../components/common/PlaceCardV2';
import { useTravelGuide } from '../context/TravelGuideContext';
import { decodePlan, encodePlan } from '../utils/itinerary';

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(3),
  },
  banner: {
    borderRadius: 26,
    border: '1px solid rgba(166, 140, 118, 0.28)',
    background: 'linear-gradient(118deg, #fff8ef 0%, #f3e7d9 60%, #ebdac9 130%)',
    boxShadow: '0 16px 32px rgba(63, 46, 31, 0.1)',
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  heading: {
    fontFamily: '"Manrope", sans-serif',
    fontWeight: 800,
    letterSpacing: '-0.02em',
    color: '#2f261f',
  },
  subheading: {
    color: '#645649',
    maxWidth: 700,
    marginTop: theme.spacing(0.8),
  },
  actionRow: {
    marginTop: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  quickStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  statCard: {
    borderRadius: 14,
    border: '1px solid rgba(166, 140, 118, 0.28)',
    background: 'rgba(255, 252, 248, 0.86)',
    padding: theme.spacing(1.4),
  },
  statLabel: {
    color: '#7b6b5d',
    fontSize: '0.8rem',
    fontWeight: 700,
  },
  statValue: {
    fontFamily: '"Manrope", sans-serif',
    fontWeight: 800,
    fontSize: '1.2rem',
    color: '#3b3027',
    marginTop: 2,
  },
  controls: {
    borderRadius: 18,
    border: '1px solid rgba(169, 144, 124, 0.28)',
    background: 'linear-gradient(180deg, #fffdf9 0%, #fbf3e9 100%)',
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  daysWrap: {
    display: 'grid',
    gap: theme.spacing(1.4),
  },
  dayCard: {
    borderRadius: 18,
    border: '1px solid rgba(169, 144, 124, 0.28)',
    background: 'linear-gradient(180deg, #fffefb 0%, #fbf4eb 100%)',
    padding: theme.spacing(1.6),
  },
  dayHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  dayTitle: {
    fontFamily: '"Manrope", sans-serif',
    fontWeight: 800,
    color: '#342a22',
  },
  dayChips: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  stopItem: {
    borderRadius: 14,
    border: '1px solid rgba(169, 144, 124, 0.24)',
    background: '#fffaf3',
    padding: theme.spacing(1.1, 1.2),
  },
  savedPanel: {
    marginTop: theme.spacing(3),
    borderRadius: 18,
    border: '1px solid rgba(169, 144, 124, 0.28)',
    background: 'linear-gradient(180deg, #fffdf9 0%, #fbf3e9 100%)',
    padding: theme.spacing(1.4),
  },
  alert: {
    borderRadius: 12,
    border: '1px solid rgba(166, 140, 118, 0.28)',
    background: '#fff9f0',
    padding: theme.spacing(1, 1.2),
    marginBottom: theme.spacing(1.2),
  },
}));

const ItineraryPage = () => {
  const classes = useStyles();
  const { state, dispatch, savedPlaceDetails } = useTravelGuide();
  const [shareMessage, setShareMessage] = useState('');
  const [newDate, setNewDate] = useState('');
  const [targetDayIndex, setTargetDayIndex] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const decoded = decodePlan(params.get('plan'));
    if (decoded?.days?.length) {
      dispatch({ type: 'SET_ITINERARY', payload: decoded });
    }
  }, [dispatch]);

  const dayLookup = useMemo(
    () => state.itinerary.days.map((day) => ({
      ...day,
      places: day.stops.map((id) => state.placeCatalog[id]).filter(Boolean),
    })),
    [state.itinerary.days, state.placeCatalog],
  );

  useEffect(() => {
    if (targetDayIndex >= dayLookup.length) {
      setTargetDayIndex(Math.max(dayLookup.length - 1, 0));
    }
  }, [targetDayIndex, dayLookup.length]);

  const totals = useMemo(() => {
    const totalStops = dayLookup.reduce((sum, day) => sum + day.stops.length, 0);
    const activeDays = dayLookup.filter((day) => day.stops.length > 0).length;
    return {
      days: dayLookup.length,
      stops: totalStops,
      activeDays,
    };
  }, [dayLookup]);

  const addDay = () => {
    if (!newDate) return;
    dispatch({ type: 'ADD_DAY', payload: newDate });
    setNewDate('');
  };

  const sharePlan = async () => {
    const token = encodePlan(state.itinerary);
    if (!token) {
      setShareMessage('Plan is too large to share as URL. Reduce stops and retry.');
      return;
    }
    const url = `${window.location.origin}/itinerary?plan=${token}`;
    try {
      await navigator.clipboard.writeText(url);
      setShareMessage('Share link copied to clipboard.');
    } catch (error) {
      setShareMessage(url);
    }
  };

  return (
    <Container maxWidth="xl" className={classes.root}>
      <Paper elevation={0} className={classes.banner}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h4" className={classes.heading}>Build your itinerary with confidence</Typography>
            <Typography variant="body1" className={classes.subheading}>
              Organize your trip day-by-day, drag your focus between stops, and share your plan instantly with one link.
            </Typography>
            <Box className={classes.actionRow}>
              <Button component={Link} to="/explore" variant="contained" color="primary">Explore Places</Button>
              <Button startIcon={<ShareIcon />} variant="outlined" color="primary" onClick={sharePlan}>
                Share itinerary
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box className={classes.quickStats}>
              <Paper elevation={0} className={classes.statCard}>
                <Typography className={classes.statLabel}>Total Days</Typography>
                <Typography className={classes.statValue}>{totals.days}</Typography>
              </Paper>
              <Paper elevation={0} className={classes.statCard}>
                <Typography className={classes.statLabel}>Total Stops</Typography>
                <Typography className={classes.statValue}>{totals.stops}</Typography>
              </Paper>
              <Paper elevation={0} className={classes.statCard}>
                <Typography className={classes.statLabel}>Planned Days</Typography>
                <Typography className={classes.statValue}>{totals.activeDays}</Typography>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} className={classes.controls}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Add travel day"
              type="date"
              size="small"
              variant="outlined"
              value={newDate}
              onChange={(event) => setNewDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button startIcon={<PlaylistAddIcon />} variant="contained" color="primary" onClick={addDay}>
              Add Day
            </Button>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box display="flex" justifyContent="flex-end" gridGap={8} flexWrap="wrap">
              <Chip icon={<DateRangeIcon />} label={`${totals.days} days`} />
              <Chip icon={<EventAvailableIcon />} label={`${totals.stops} planned stops`} />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {shareMessage && (
        <Paper elevation={0} className={classes.alert}>
          <Typography variant="body2">{shareMessage}</Typography>
        </Paper>
      )}

      {dayLookup.length === 0 ? (
        <EmptyState
          title="No days in itinerary"
          description="Add a day and start planning."
        />
      ) : (
        <div className={classes.daysWrap}>
          {dayLookup.map((day, dayIndex) => (
            <Paper key={`${day.date}-${dayIndex}`} elevation={0} className={classes.dayCard}>
              <Box className={classes.dayHeader}>
                <Typography variant="h6" className={classes.dayTitle}>
                  Day {dayIndex + 1} - {day.date}
                </Typography>
                <Box className={classes.dayChips}>
                  <Chip size="small" label={`${day.stops.length} stops`} />
                </Box>
              </Box>

              {day.places.length === 0 ? (
                <Box mt={1.4}>
                  <EmptyState
                    title="No stops yet"
                    description="Add saved places to this day from the cards below."
                  />
                </Box>
              ) : (
                <Grid container spacing={1.4} style={{ marginTop: 4 }}>
                  {day.places.map((place, idx) => (
                    <Grid item xs={12} md={6} key={`${place.id}-${idx}`}>
                      <Paper elevation={0} className={classes.stopItem}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="subtitle1" style={{ fontWeight: 700 }}>{place.name}</Typography>
                            <Typography variant="caption" color="textSecondary">{place.address || 'Address unavailable'}</Typography>
                          </Box>
                          <Box>
                            <IconButton
                              size="small"
                              onClick={() => dispatch({ type: 'MOVE_STOP', payload: { dayIndex, fromIndex: idx, toIndex: idx - 1 } })}
                            >
                              <KeyboardArrowUpIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => dispatch({ type: 'MOVE_STOP', payload: { dayIndex, fromIndex: idx, toIndex: idx + 1 } })}
                            >
                              <KeyboardArrowDownIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => dispatch({ type: 'REMOVE_STOP', payload: { dayIndex, placeId: place.id } })}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Paper>
          ))}
        </div>
      )}

      <Paper elevation={0} className={classes.savedPanel}>
        <Box mb={1.2} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gridGap={8}>
          <Typography variant="h6" style={{ fontFamily: '"Manrope", sans-serif', fontWeight: 800 }}>
            Add from saved places
          </Typography>
          <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
            <Select value={targetDayIndex} onChange={(event) => setTargetDayIndex(Number(event.target.value))}>
              {dayLookup.map((day, idx) => (
                <MenuItem key={`${day.date}-${idx}`} value={idx}>
                  Day {idx + 1} - {day.date}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {savedPlaceDetails.length === 0 ? (
          <EmptyState
            title="No saved places"
            description="Save places first, then add them to itinerary days."
            actionLabel="Go to Explore"
            actionTo="/explore"
          />
        ) : (
          <Grid container spacing={2}>
            {savedPlaceDetails.map((place) => (
              <Grid item xs={12} md={6} lg={4} key={place.id}>
                <PlaceCardV2
                  place={place}
                  compact
                  onAddToDay={(placeId) => dispatch({ type: 'ADD_STOP', payload: { dayIndex: targetDayIndex, placeId } })}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>
    </Container>
  );
};

export default ItineraryPage;
